import { Typography, Space, Flex, Row, Col, Divider, Input, Checkbox, message, Table, InputNumber, Button, Select, Alert } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import CustomCard from "../../../components/CustomCard/CustomCard";
import { LABEL_TXT, BTN_TXT, REASON_MODAL_CONFIG, DEAL_APPROVAL_SUCCESS_MESSAGES, getDealApprovalSuccessMessage, DEAL_STATUS_LABEL_MAP } from "../../../constants/constants";
import DealDetailNoSpace from "../../../components/Details/DealDetailNoSpace";
import DealDetail from "../../../components/Details/DealDetail";
import { capitalizeWords, convertToPercentage, formatCurrency, calculateRelativeTime, resolveMaxFundRequiredPct, maxFundAllowedAmount } from "../../../helpers/helpers";
import { useState, useCallback, useEffect } from "react";
import FormFooter from "../../../components/CustomForms/FormFooter"
import { useSelector, useDispatch } from "react-redux";
import { approvependingdeal, Rejectdeal } from "../../../redux/slices/dealsSlice"
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../components/CustomModal/CustomModal"
import ReasonForm from "../../../components/CustomForms/ReasonForm"
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

const ApproveDeal = ({ deal }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isSupplyChain = isSupplyChainCategory(
    deal?.dealDetails?.dealCategoryId || deal?.dealDetails?.dealType,
  );
  const { approveSubmitLoading, error, cancelError, cancelLoading } = useSelector((state) => state?.deals);
  const [approvedAmtErr, setApprovedAmtErr] = useState(null);
  const [checkedDetails, setCheckedDetails] = useState({})
  const [dealConstraints, setDealConstraints] = useState({
    approvedAmt: "",
    expiryDays: "",
    dealType: "",
    dealVisibility: "",
    // lineOfBus: ""
  })
  const [supplierAllocations, setSupplierAllocations] = useState(() => {
    const list = deal?.dealDetails?.supplierAllocations || [];
    return list.map((item) => ({
      supplierId: item.supplierId,
      supplierName: item.supplierName,
      allocatedAmount: Number(item.allocatedAmount || 0),
    }));
  });
  const [modal, setModal] = useState({ type: null, data: null });

  const closeModal = useCallback(() => setModal({ type: null, data: null }), []);
  const openModal = useCallback((type, data) => setModal({ type, data }), []);

  useEffect(() => {
    if (deal?.dealDetails?.supplierAllocations) {
      setSupplierAllocations(
        deal.dealDetails.supplierAllocations.map((item) => ({
          supplierId: item.supplierId,
          supplierName: item.supplierName,
          allocatedAmount: Number(item.allocatedAmount || 0),
        }))
      );
    }
  }, [deal?.dealDetails?.supplierAllocations]);

  const allFilled = Object.values(dealConstraints).every((val) => val !== "" && val !== null && val !== undefined);

  const maxFundRequiredPct = resolveMaxFundRequiredPct(deal?.dealDetails?.maxFundRequiredPct);
  const maxFundPctLabel = parseFloat((maxFundRequiredPct * 100).toFixed(2));
  const approvedLimit = maxFundAllowedAmount(
    deal?.dealDetails?.totalInvoiceAmt,
    maxFundRequiredPct,
  ).toFixed(2);
  const fundRequired = parseFloat(deal?.dealDetails?.fundRequired);
  const availableCreditBalanceAmt = parseFloat(deal?.dealDetails?.availableCreditBalanceAmt);
  const totalCreditLimit = parseFloat(deal?.dealDetails?.totalCreditLimit);

  const handleConstraintsChange = (key, value) => {
    if (key === "approvedAmt") {
      const numValue = parseFloat(value);
      let errorMsg = null;

      if (isNaN(numValue) || numValue < 80000) {
        errorMsg = "Approved amount must be greater than or equal to 80,000";
      } else if (numValue > approvedLimit) {
        errorMsg = `Approved amount must be less than or equal to ${maxFundPctLabel}% of the ${isSupplyChain ? "Purchase Order" : "Invoice"} Amount (${formatCurrency(approvedLimit)})`;
      } else if (numValue > fundRequired) {
        errorMsg = `Approved amount cannot exceed fund required (${formatCurrency(fundRequired)})`;
      } else if (numValue > availableCreditBalanceAmt) {
        errorMsg = `Approved amount cannot exceed available credit balance (${formatCurrency(availableCreditBalanceAmt)})`;
      } else if (numValue > totalCreditLimit) {
        errorMsg = `Approved amount cannot exceed total credit limit (${formatCurrency(totalCreditLimit)})`;
      }

      setApprovedAmtErr(errorMsg);
    }

    setDealConstraints((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSupplierAmountChange = (supplierId, newAmount) => {
    const amt = Number(newAmount || 0);
    setSupplierAllocations((prev) =>
      prev.map((item) => {
        if (item.supplierId === supplierId) {
          return {
            ...item,
            allocatedAmount: amt,
          };
        }
        return item;
      })
    );
  };

  const handleSupplierChange = (oldSupplierId, newSupplierId, newSupplierName) => {
    setSupplierAllocations((prev) =>
      prev.map((item) => {
        if (item.supplierId === oldSupplierId) {
          return {
            ...item,
            supplierId: newSupplierId,
            supplierName: newSupplierName,
          };
        }
        return item;
      })
    );
  };

  const handleSplitEvenly = () => {
    if (!supplierAllocations.length) return;
    const approvedAmtNum = parseFloat(dealConstraints.approvedAmt) || 0;
    const count = supplierAllocations.length;
    const splitAmt = Number((approvedAmtNum / count).toFixed(2));

    setSupplierAllocations((prev) =>
      prev.map((item, index) => {
        let amt = splitAmt;
        if (index === count - 1) {
          amt = Number((approvedAmtNum - splitAmt * (count - 1)).toFixed(2));
        }
        return {
          ...item,
          allocatedAmount: amt,
        };
      })
    );
  };

  const handleAddSupplier = () => {
    const allocatedIds = new Set(supplierAllocations.map((s) => s.supplierId));
    const unallocated = (deal?.availableSuppliers || []).find(
      (s) => !allocatedIds.has(s.supplierId)
    );
    if (unallocated) {
      setSupplierAllocations((prev) => [
        ...prev,
        {
          supplierId: unallocated.supplierId,
          supplierName: unallocated.supplierName,
          allocatedAmount: 0,
        },
      ]);
    }
  };

  const handleRemoveSupplier = (supplierId) => {
    setSupplierAllocations((prev) =>
      prev.filter((item) => item.supplierId !== supplierId)
    );
  };

  const totalAllocatedAmount = supplierAllocations.reduce(
    (sum, item) => sum + Number(item.allocatedAmount || 0),
    0
  );
  const approvedAmtNum = parseFloat(dealConstraints.approvedAmt);
  const totalAllocatedPercentage =
    !isNaN(approvedAmtNum) && approvedAmtNum > 0
      ? Number(((totalAllocatedAmount / approvedAmtNum) * 100).toFixed(2))
      : 0;
  const remainingAmount =
    !isNaN(approvedAmtNum) && approvedAmtNum > 0
      ? Number((approvedAmtNum - totalAllocatedAmount).toFixed(2))
      : 0;

  let supplierError = null;
  if (isSupplyChain && supplierAllocations.length > 0 && !isNaN(approvedAmtNum) && approvedAmtNum > 0) {
    const hasZeroAllocation = supplierAllocations.some(
      (s) => Number(s.allocatedAmount || 0) <= 0
    );
    if (hasZeroAllocation) {
      supplierError = "Each supplier must have an allocated amount greater than 0";
    } else if (Math.abs(totalAllocatedAmount - approvedAmtNum) > 1) {
      supplierError = "Total supplier allocated amount must equal the approved amount";
    }
  }

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckedDetails((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleApproveClick = async () => {
    const checkedIds = Object.entries(checkedDetails)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    const payload = {
      dealId: deal?.dealDetails?.dealId,
      expiryDays: dealConstraints.expiryDays,
      approvedAmt: dealConstraints.approvedAmt,
      dealTypeId: dealConstraints.dealType,
      // lineOfBusinessId: dealConstraints.lineOfBus,
      visibilityId: dealConstraints.dealVisibility,
      checkbox: checkedIds
    };

    if (isSupplyChain && supplierAllocations.length > 0) {
      const approvedAmt = parseFloat(dealConstraints.approvedAmt) || 0;
      payload.supplierAllocations = supplierAllocations.map((alloc) => {
        const amt = Number(Number(alloc.allocatedAmount || 0).toFixed(2));
        const pct = approvedAmt > 0 ? Number(((amt / approvedAmt) * 100).toFixed(2)) : 0;
        return {
          supplierId: alloc.supplierId,
          supplierName: alloc.supplierName,
          percentage: pct,
          allocatedPercentage: pct,
          amount: amt,
          allocatedAmount: amt,
        };
      });

      if (payload.supplierAllocations.length > 0 && Math.abs(totalAllocatedAmount - approvedAmt) < 0.01) {
        const currentSum = payload.supplierAllocations.reduce((sum, s) => sum + s.allocatedPercentage, 0);
        const diff = Number((100 - currentSum).toFixed(2));
        if (Math.abs(diff) <= 0.05) {
          const lastIdx = payload.supplierAllocations.length - 1;
          const adjustedPct = Number((payload.supplierAllocations[lastIdx].allocatedPercentage + diff).toFixed(2));
          payload.supplierAllocations[lastIdx].allocatedPercentage = adjustedPct;
          payload.supplierAllocations[lastIdx].percentage = adjustedPct;
        }
      }
    }

    const result = await dispatch(approvependingdeal(payload));
    if (result.payload?.statusCode === 201) {
      message.success(
        getDealApprovalSuccessMessage(
          DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_APPROVED,
          deal?.dealDetails?.dealId,
        ),
      );
      navigate(-1);
      return;
    }
    message.error("Failed to approve deal. Please try again.");
  }

  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.dealId) return;
      const payload = {
        dealId: modal.data.dealId,
        comments: values.reason || "",
        modifiedAt: modal.data.modifiedAt,
      };

      const res = await dispatch(Rejectdeal(payload));
      if (res.payload?.statusCode === 201) {
        navigate(-1)
        closeModal();
      }
    },
    [modal.type, modal.data?.dealId, modal.data?.modifiedAt, dispatch, navigate, closeModal]
  );


  return (
    <CustomCard className="approve-deal-card-container">
      <div className="deal-header-container">
        <Space direction="vertical" size={10}>
          <DealDetailNoSpace
            label={LABEL_TXT.BUS_NAME}
            value={deal?.dealDetails?.orgName}
            recourseTag={<Text className="recourse-on-tag">Recourse on</Text>}
          />
          <DealDetailNoSpace
            label={LABEL_TXT.ENT_NAME}
            value={deal?.dealDetails?.entName}
          />
        </Space>
        <Space direction="vertical" size={10}>
          <DealDetailNoSpace
            label={LABEL_TXT.DEAL_STATUS}
            value={
              <Text className="deal-status-tag">
                {DEAL_STATUS_LABEL_MAP[deal?.dealDetails?.statusId] ||
                  (String(deal?.dealDetails?.statusName).toLowerCase() === "repaid"
                    ? "Partially Settled"
                    : capitalizeWords(deal?.dealDetails?.statusName))}
              </Text>
            }
          />
          <DealDetailNoSpace
            label={LABEL_TXT.DEAL_ID}
            value={deal?.dealDetails?.dealId}
          />
        </Space>
      </div>

      <div className="deal-constraints-container">
        <Flex align="center" justify="space-between">
          <DealDetailNoSpace
            label="Interest Rate"
            value={convertToPercentage(deal?.dealDetails?.annualDiscountRate)}
          />
          <DealDetailNoSpace label="Tenure" value={`${deal?.dealDetails?.tenure} days`} />
        </Flex>
      </div>

      <Row className="details-row-container" gutter={[0, 0]}>
        <Col xs={24} md={12} className="details-equal-col">
          <div className="details-col-container">
            <DealDetail
              label="Total Credit Limit"
              value={formatCurrency(deal?.dealDetails?.totalCreditLimit)}
              height="46.6px"
            />
            <DealDetail
              label="Available Credit Balance Amount"
              value={formatCurrency(deal?.dealDetails?.availableCreditBalanceAmt)}
              height="46.6px"
            />
            <DealDetail
              label={isSupplyChain ? "Purchase Order Amount" : "Invoice Amount"}
              value={formatCurrency(deal?.dealDetails?.totalInvoiceAmt)}
              height="46.6px"
            />
            {isSupplyChain && (
              <DealDetail
                label="Material Cost"
                value={formatCurrency(deal?.dealDetails?.totalMaterialCost)}
                height="46.6px"
              />
            )}
            <DealDetail
              label="Fund Required Amount"
              value={formatCurrency(deal?.dealDetails?.fundRequired)}
              height="46.6px"
            />
            {/* <DealDetail
              label="Minimum Deal Amount"
              value={formatCurrency(deal?.dealDetails?.minDealAmt)}
              height="46.6px"
            /> */}
          </div>
        </Col>

        <Col
          xs={0}
          md={1}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Divider
            type="vertical"
            style={{
              height: "100%",
              borderLeft: "1px solid #CBD5E2",
              margin: 0,
            }}
          />
        </Col>

        <Col xs={24} md={11} className="details-equal-col">
          <div className="details-col-container">
            <DealDetail
              label="Approved Amount"
              isInput={true}
              name="approvedAmt"
              value={dealConstraints.approvedAmt}
              onchange={(val) => handleConstraintsChange("approvedAmt", val)}
            />
            <DealDetail
              label="Expiry Days"
              isSelect={true}
              name="expiryDays"
              value={dealConstraints.expiryDays || null}
              onchange={(val) => handleConstraintsChange("expiryDays", val)}
              options={deal?.expiryDays?.map((days) => ({
                value: days,
                label: days,
              }))}
            />
            <DealDetail
              label="Deal Type"
              isSelect={true}
              name="dealType"
              value={dealConstraints.dealType || null}
              onchange={(val) => handleConstraintsChange("dealType", val)}
              options={deal?.dealTypeMaster?.map((type) => ({
                value: type?.dealTypeId,
                label: capitalizeWords(type?.dealTypeName),
              }))}
            />
            <DealDetail
              label="Deal Visibility"
              isSelect={true}
              name="dealVisibility"
              value={dealConstraints.dealVisibility || null}
              onchange={(val) => handleConstraintsChange("dealVisibility", val)}
              options={deal?.visibility?.map((deal) => ({
                value: deal?.visibilityId,
                label: capitalizeWords(deal?.visibilityName),
              }))}
            />
            {/* <DealDetail
              label="Line of Business"
              isSelect={true}
              name="lineOfBus"
              value={dealConstraints.lineOfBus || null}
              onchange={(val) => handleConstraintsChange("lineOfBus", val)}
              options={deal?.lineOfBusiness?.map((item) => ({
                value: item?.lineOfBusinessId,
                label: capitalizeWords(item?.lineOfBusinessName),
              }))}
            /> */}
          </div>
        </Col>
      </Row>

      <CustomCard className="fee-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="fee-detail">
              <DealDetail
                label="Platform Fees"
                value={convertToPercentage(deal?.dealDetails?.platformFee)}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="fee-detail">
              <DealDetail
                label="Miscellaneous Fee"
                value={convertToPercentage(deal?.dealDetails?.miscellaneousFees)}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="fee-detail">
              <DealDetail
                label="Stamp Fees"
                value={formatCurrency(deal?.dealDetails?.stampFee)}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="fee-detail">
              <DealDetail
                label="Penal Charges"
                value={convertToPercentage(deal?.dealDetails?.penalCharge)}
              />
            </div>
          </Col>
        </Row>
      </CustomCard>

      {isSupplyChain && (supplierAllocations?.length > 0 || deal?.dealDetails?.supplierAllocations?.length > 0) && (
        <CustomCard className="supplier-breakdown-container">
          <Flex align="center" justify="space-between" style={{ marginBottom: 12 }}>
            <Text className="supplier-breakdown-title">Supplier Breakdown</Text>
            <Space>
              <Button type="link" onClick={handleSplitEvenly} style={{ padding: 0 }}>
                Split Evenly
              </Button>
              {deal?.availableSuppliers?.length > supplierAllocations?.length && (
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddSupplier}
                >
                  Add Supplier
                </Button>
              )}
            </Space>
          </Flex>
          <Table
            dataSource={supplierAllocations}
            rowKey={(record) => record.supplierId}
            pagination={false}
            tableLayout="fixed"
            columns={[
              {
                title: "Supplier Name",
                dataIndex: "supplierName",
                key: "supplierName",
                align: "left",
                width: "35%",
                render: (val, record) => {
                  const options =
                    deal?.availableSuppliers?.map((s) => ({
                      value: s.supplierId,
                      label: s.supplierName,
                    })) || [];
                  if (options.length > 0) {
                    return (
                      <Select
                        style={{ width: "100%" }}
                        value={record.supplierId}
                        options={options}
                        onChange={(newId) => {
                          const selected = options.find((o) => o.value === newId);
                          handleSupplierChange(
                            record.supplierId,
                            newId,
                            selected?.label || newId
                          );
                        }}
                      />
                    );
                  }
                  return <Text strong>{record.supplierName || record.supplierId}</Text>;
                },
              },
              {
                title: "Allocated Amount",
                dataIndex: "allocatedAmount",
                key: "allocatedAmount",
                align: "right",
                width: "30%",
                render: (val, record) => (
                  <InputNumber
                    min={0}
                    prefix="₹"
                    value={val}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                    onChange={(newVal) =>
                      handleSupplierAmountChange(record.supplierId, newVal)
                    }
                  />
                ),
              },
              {
                title: "Allocation (%)",
                key: "calculatedPercentage",
                align: "center",
                width: "25%",
                render: (_, record) => {
                  const amt = Number(record.allocatedAmount || 0);
                  const pct =
                    !isNaN(approvedAmtNum) && approvedAmtNum > 0
                      ? ((amt / approvedAmtNum) * 100).toFixed(2)
                      : "0.00";
                  return <Text strong>{pct}%</Text>;
                },
              },
              ...(supplierAllocations.length > 1
                ? [
                    {
                      title: "",
                      key: "action",
                      width: "10%",
                      align: "center",
                      render: (_, record) => (
                        <Button
                          type="text"
                          danger
                          aria-label="Remove supplier"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveSupplier(record.supplierId)}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />

          <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
            <Col>
              <Space size={16}>
                <Text type="secondary">
                  Total Allocated:{" "}
                  <Text strong>{formatCurrency(totalAllocatedAmount)}</Text>
                </Text>
                <Text type="secondary">
                  Total Percentage:{" "}
                  <Text
                    strong
                    type={
                      Math.abs(totalAllocatedPercentage - 100) > 0.1
                        ? "danger"
                        : "success"
                    }
                  >
                    {totalAllocatedPercentage.toFixed(2)}%
                  </Text>
                </Text>
                {!isNaN(approvedAmtNum) && approvedAmtNum > 0 && (
                  <Text type="secondary">
                    Remaining:{" "}
                    <Text
                      strong
                      type={
                        remainingAmount < 0
                          ? "danger"
                          : remainingAmount === 0
                          ? "success"
                          : "warning"
                      }
                    >
                      {formatCurrency(remainingAmount)}
                    </Text>
                  </Text>
                )}
              </Space>
            </Col>
          </Row>

          {supplierError && (
            <Alert
              type="error"
              showIcon
              message={supplierError}
              style={{ marginTop: 12 }}
            />
          )}
        </CustomCard>
      )}

      <Input.TextArea
        name="entDesc"
        disabled
        value={deal?.dealDetails?.entDescription}
        style={{ marginTop: 20 }}
      />

      <Input.TextArea
        name="historyWithKarncy"
        disabled
        value={
          `For ${calculateRelativeTime(deal?.dealDetails?.orgCreatedAt)}, this business has actively utilized the Karncy platform to create value and drive impact.`
        }
        style={{ marginTop: 20 }}
      />

      <Space direction="vertical" style={{ marginTop: 30, width: "100%" }}>
        {deal?.checkboxDetails?.map((checkbox) => (
          <Checkbox
            key={checkbox.checkboxId}
            name={checkbox.checkboxId}
            checked={checkedDetails[checkbox.checkboxId] || false}
            onChange={handleCheckboxChange}
          >
            {checkbox.checkboxName}
          </Checkbox>
        ))}
      </Space>

      {modal.type && (
        <CustomModal
          title={`${modal.type.charAt(0).toUpperCase() + modal.type.slice(1)
            } Deal`}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG[modal.type]?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
              btnType={modal.type === "reject" || modal.type === "cancel" ? "danger" : "primary"}
              submitLoading={cancelLoading}
              submitError={cancelError}
              onClose={closeModal}
              handleOnFormSubmit={handleReasonSubmit}
            />
          }
        />
      )}

      <FormFooter
        submitLoading={approveSubmitLoading}
        rejectBtn={true}
        btnName={BTN_TXT.APPROVE}
        btnType="approve"
        onSubmitClick={handleApproveClick}
        onCancelClick={() => openModal("reject", deal?.dealDetails)}
        disabled={Boolean(approvedAmtErr) || Boolean(supplierError) || !allFilled}
        error={approvedAmtErr || supplierError || error}
      />
    </CustomCard>
  );
};

export default ApproveDeal;
